import Loader from '@sportnet/ui/Loader';
import Segment from '@sportnet/ui/Segment';
import { Table, Tbody, Td, Th, Thead, Tr } from '@sportnet/ui/Table';
import SportnetUiAppContext from '@sportnet/ui/TheLayout/AppContext';
import Sidebar from '@sportnet/ui/Sidebar';
import Button from '@sportnet/ui/Button';
import FormField from '@sportnet/ui/FormField';
import Label from '@sportnet/ui/Label/Label';
import {
  ContextBar,
  ContextBarItem,
  ContextBarSpacer,
} from '@sportnet/ui/ContextBar';
import React, { useState, useEffect } from 'react';
import Api from '../../Api';
import Layout from '../../components/Layout';
import { LoaderContainer } from '../../components/List';
import { __ } from '../../utilities';
import getApiError from '../../utilities/getApiError';
import { useAuth } from '@sportnet/auth-react';
import formatDate from '../../utilities/formatDate';
import config from '../../config';
import RolesSelector from './RolesSelector';

const CopyIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const ApiKeysList = () => {
  const { ppo: appSpace } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [apps, setApps] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingApps, setIsFetchingApps] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [editedRoles, setEditedRoles] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [copiedKeyId, setCopiedKeyId] = useState(null);

  useEffect(() => {
    const fetchApiKeys = async () => {
      setIsFetching(true);
      try {
        const res = await Api.getApiKeys(appSpace);
        setApiKeys(res.items || []);
      } catch (e) {
        const err = getApiError(e);
        alert(
          `Chyba získania API kľúčov: ${err.details.statusCode} ${err.details.name} ${err.message}`,
        );
      } finally {
        setIsFetching(false);
      }
    };

    const fetchApps = async () => {
      setIsFetchingApps(true);
      try {
        // Načítame primary aj systémové (admin, mcp) apky, aby sa v zozname
        // kľúčov korektne zobrazili názvy aplikácií a rolí pre obe skupiny.
        const [primaryRes, systemRes] = await Promise.all([
          Api.getPublicApps({ graph: true }),
          Api.getPublicApps({
            graph: true,
            $queryParameters: { flags: 'system' },
          }),
        ]);
        const allApps = [...(primaryRes.apps || []), ...(systemRes.apps || [])];

        // Create flat map with both primary and secondary apps
        const flatApps = [];
        allApps.forEach(app => {
          if (
            !flatApps.find(a => a._id === app._id || a.app_id === app.app_id)
          ) {
            flatApps.push(app);
          }
          // Add secondary apps from childApps
          (app.child_apps || []).forEach(childApp => {
            if (
              childApp &&
              !flatApps.find(
                a => a._id === childApp._id || a.app_id === childApp._id,
              )
            ) {
              flatApps.push(childApp);
            }
          });
        });

        setApps(flatApps);
      } catch (e) {
        const err = getApiError(e);
        alert(
          `Chyba získania aplikácií: ${err.details.statusCode} ${err.details.name} ${err.message}`,
        );
      } finally {
        setIsFetchingApps(false);
      }
    };

    if (appSpace) {
      fetchApiKeys();
      fetchApps();
    }
  }, [appSpace]);

  const copyToClipboard = async (text, keyId = 'sidebar') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKeyId(keyId);
      setTimeout(() => setCopiedKeyId(null), 1500);
    } catch (err) {
      // Fallback pre staršie prehliadače
      console.error('Chyba pri kopírovaní:', err);
    }
  };

  const truncateApiKey = apiKey => {
    if (!apiKey || apiKey.length <= 48) return apiKey;
    const start = apiKey.substring(0, 24);
    const end = apiKey.substring(apiKey.length - 20);
    return `${start}...${end}`;
  };

  const getAppName = appId => {
    const app = apps.find(a => a._id === appId || a.app_id === appId);
    return app ? app.name : appId;
  };

  const getAppDocsUrl = appId => {
    const app = apps.find(a => a._id === appId || a.app_id === appId);
    return app?.apiDocsUrl || null;
  };

  const getRoleName = (appId, roleId) => {
    const app = apps.find(a => a._id === appId);
    if (!app || !app.roles) return roleId;
    const role = app.roles.find(r => r.role === roleId);
    return role ? role.name : roleId;
  };

  const renderAppRoles = appRoles => {
    if (!appRoles || appRoles.length === 0) {
      return <span>{__('Žiadne role')}</span>;
    }

    return (
      <div>
        {appRoles.map((appRole, index) => {
          const appId = appRole.app_id || appRole.appId;
          const roleId = appRole.role_id || appRole.roleId || appRole.role;
          const childAppsRole = appRole.child_apps_role || [];
          const docsUrl = getAppDocsUrl(appId);

          return (
            <div key={index} style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <strong>{getAppName(appId)}</strong>
                {docsUrl && (
                  <a
                    href={docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{
                      marginLeft: '6px',
                      fontSize: '12px',
                      color: '#0066cc',
                    }}
                    title={__('API dokumentácia')}
                  >
                    [docs]
                  </a>
                )}
                <span style={{ marginLeft: '4px' }}>
                  : {getRoleName(appId, roleId)}
                </span>
              </div>
              {childAppsRole.length > 0 && (
                <div style={{ marginLeft: '16px', marginTop: '4px' }}>
                  {childAppsRole.map((child, childIndex) => {
                    const childAppId = child.app_id || child.appId;
                    const childRoleId =
                      child.role_id || child.roleId || child.role;
                    const childDocsUrl = getAppDocsUrl(childAppId);
                    return (
                      <div
                        key={childIndex}
                        style={{
                          fontSize: '12px',
                          color: '#666',
                          marginBottom: '2px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <span style={{ marginRight: '4px' }}>└</span>
                        {getAppName(childAppId)}
                        {childDocsUrl && (
                          <a
                            href={childDocsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{
                              marginLeft: '4px',
                              fontSize: '11px',
                              color: '#0066cc',
                            }}
                            title={__('API dokumentácia')}
                          >
                            [docs]
                          </a>
                        )}
                        <span style={{ marginLeft: '4px' }}>
                          : {getRoleName(childAppId, childRoleId)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const openDetail = item => {
    setIsCreating(false);
    setActiveItem(item);
    setEditedName(item.name || '');
    setEditedRoles(item.appRoles || []);
    setSidebarVisible(true);
  };

  const openCreateSidebar = () => {
    setIsCreating(true);
    setActiveItem(null);
    setEditedName('');
    setEditedRoles([]);
    setSidebarVisible(true);
  };

  const closeSidebar = () => {
    setSidebarVisible(false);
    setActiveItem(null);
    setEditedName('');
    setEditedRoles([]);
    setIsCreating(false);
  };

  const handleUpdate = async () => {
    if (!activeItem) return;

    setIsSaving(true);
    try {
      await Api.updateApiKey(appSpace, activeItem._id, {
        data: {
          name: editedName,
          appRoles: editedRoles,
        },
      });
      closeSidebar();
      // Refresh list
      const res = await Api.getApiKeys(appSpace);
      setApiKeys(res.items || []);
    } catch (e) {
      const err = getApiError(e);
      alert(
        `Chyba pri aktualizácii API kľúča: ${err.details.statusCode} ${err.details.name} ${err.message}`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!editedName) {
      alert(__('Zadajte názov API kľúča'));
      return;
    }

    setIsSaving(true);
    try {
      await Api.createApiKey(appSpace, {
        data: {
          name: editedName,
          appRoles: editedRoles,
        },
      });
      closeSidebar();
      // Refresh list
      const res = await Api.getApiKeys(appSpace);
      setApiKeys(res.items || []);
    } catch (e) {
      const err = getApiError(e);
      alert(
        `Chyba pri vytváraní API kľúča: ${err.details.statusCode} ${err.details.name} ${err.message}`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevoke = async () => {
    if (!activeItem) return;

    if (!window.confirm(__('Naozaj chcete zrušiť tento API kľúč?'))) {
      return;
    }

    setIsSaving(true);
    try {
      await Api.deleteApiKey(appSpace, activeItem._id);
      closeSidebar();
      // Refresh list
      const res = await Api.getApiKeys(appSpace);
      setApiKeys(res.items || []);
    } catch (e) {
      const err = getApiError(e);
      alert(
        `Chyba pri rušení API kľúča: ${err.details.statusCode} ${err.details.name} ${err.message}`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const isLoading = isFetching && apiKeys.length === 0;

  return (
    <Layout
      bottomFixed={
        <ContextBar>
          <ContextBarSpacer />
          <ContextBarItem>
            <Button
              primary
              onClick={openCreateSidebar}
              icon="plus"
              type="button"
            >
              {__('Vytvoriť API kľúč')}
            </Button>
          </ContextBarItem>
        </ContextBar>
      }
    >
      <Segment noBottomGutter>
        <Segment raised noBottomGutter>
          <div style={{ padding: '16px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '12px' }}>
              {__('Ako používať API kľúče')}
            </h3>
            <p style={{ marginBottom: '12px' }}>
              {__(
                'API kľúče slúžia na autentifikáciu požiadaviek na príslušné API. Pre použitie API kľúča ho pridajte do HTTP hlavičky vašej požiadavky:',
              )}
            </p>
            <code
              style={{
                display: 'block',
                background: '#f5f5f5',
                padding: '12px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
                marginBottom: '12px',
              }}
            >
              Authorization: ApiKey {'{'}apikey{'}'}
            </code>
            <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
              {__('Príklad: Authorization: ApiKey abc123def456...')}
            </p>
          </div>
        </Segment>
      </Segment>
      <Segment noBottomGutter>
        <Segment raised loading={isLoading}>
          {isLoading ? (
            <LoaderContainer>
              <Loader size="xl" />
            </LoaderContainer>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>{__('API Key')}</Th>
                  <Th>{__('Role v aplikáciách')}</Th>
                  <Th>{__('Vytvoril')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {apiKeys.map(item => {
                  return (
                    <Tr key={item._id} onClick={() => openDetail(item)}>
                      <Td>
                        <div style={{ marginBottom: '8px' }}>
                          <strong>{item.name || __('Bez názvu')}</strong>
                        </div>
                        <code
                          onClick={e => {
                            e.stopPropagation();
                            copyToClipboard(item.apiKey, item._id);
                          }}
                          style={{
                            background:
                              copiedKeyId === item._id ? '#d4edda' : '#f5f5f5',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'background-color 0.3s ease',
                          }}
                          title={__('Kliknutím skopírujete do schránky')}
                        >
                          {truncateApiKey(item.apiKey)}
                          <CopyIcon size={14} />
                        </code>
                      </Td>
                      <Td>
                        {isFetchingApps ? (
                          <Loader size="sm" />
                        ) : (
                          renderAppRoles(item.appRoles)
                        )}
                      </Td>
                      <Td>
                        <div>
                          {item.createdBy?.displayName ||
                            item.createdBy?.sportnetId ||
                            '-'}
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#666',
                            marginTop: '4px',
                          }}
                        >
                          {item.createdAt
                            ? formatDate(item.createdAt, config.DATE_FORMAT)
                            : '-'}
                        </div>
                      </Td>
                    </Tr>
                  );
                })}
                {apiKeys.length === 0 && !isLoading && (
                  <Tr>
                    <Td
                      colSpan={3}
                      style={{ textAlign: 'center', padding: '20px' }}
                    >
                      {__('Zatiaľ nemáte vytvorené žiadne API kľúče')}
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          )}
        </Segment>
      </Segment>
      <Sidebar
        visible={sidebarVisible}
        header={isCreating ? __('Vytvoriť API kľúč') : __('Detail API kľúča')}
        onClose={closeSidebar}
      >
        <div style={{ padding: '20px' }}>
          {!isCreating && activeItem && (
            <div style={{ marginBottom: '20px' }}>
              <Label>{__('API Key')}</Label>
              <code
                onClick={() => copyToClipboard(activeItem.apiKey, 'sidebar')}
                style={{
                  background: copiedKeyId === 'sidebar' ? '#d4edda' : '#f5f5f5',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  wordBreak: 'break-all',
                  transition: 'background-color 0.3s ease',
                }}
                title={__('Kliknutím skopírujete do schránky')}
              >
                <span>{activeItem.apiKey}</span>
                <CopyIcon size={16} />
              </code>
            </div>
          )}

          <FormField
            label={__('Názov')}
            value={editedName}
            onChange={e => setEditedName(e.target.value)}
            placeholder={__('Názov API kľúča')}
            disabled={isSaving}
            required
          />

          <RolesSelector
            appSpace={appSpace}
            selectedRoles={editedRoles}
            onChange={setEditedRoles}
            disabled={isSaving}
          />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '20px',
            }}
          >
            {!isCreating ? (
              <>
                <Button
                  onClick={handleRevoke}
                  disabled={isSaving}
                  danger
                  type="button"
                  icon="trash"
                >
                  {__('Revoke')}
                </Button>
                <Button
                  onClick={handleUpdate}
                  disabled={isSaving}
                  primary
                  type="button"
                >
                  {__('Uložiť')}
                </Button>
              </>
            ) : (
              <>
                <div />
                <Button
                  onClick={handleCreate}
                  disabled={isSaving}
                  primary
                  type="button"
                >
                  {__('Vytvoriť')}
                </Button>
              </>
            )}
          </div>
        </div>
      </Sidebar>
      <SportnetUiAppContext title={__('API Keys')} />
    </Layout>
  );
};

export default ApiKeysList;
